import React, { useState, useEffect } from 'react';
import { Calendar, Calculator } from 'lucide-react';
import { SEO } from '../components/SEO';

interface WorkExperienceState {
  startDate: string;
  endDate: string;
  includeEndDate: boolean;
  additionalPeriods: {
    id: number;
    startDate: string;
    endDate: string;
  }[];
  benefitMultiplier: '1' | '1.25' | '1.5';
}

interface WorkExperienceResult {
  years: number;
  months: number;
  days: number;
  totalDays: number;
  benefitYears: number;
  benefitMonths: number;
  benefitDays: number;
  retirementEligible: boolean;
  retirementMessage: string;
}

function KalkulatorRadnogStaza() {
  const [values, setValues] = useState<WorkExperienceState>({
    startDate: '',
    endDate: new Date().toISOString().split('T')[0], // Today's date
    includeEndDate: true,
    additionalPeriods: [],
    benefitMultiplier: '1'
  });

  const [results, setResults] = useState<WorkExperienceResult | null>(null);
  const [nextId, setNextId] = useState<number>(1);

  const calculateWorkExperience = () => {
    if (!values.startDate) return null;

    const startDate = new Date(values.startDate);
    const endDate = values.endDate ? new Date(values.endDate) : new Date();
    
    // Add one day if including end date
    const adjustedEndDate = new Date(endDate);
    if (values.includeEndDate) {
      adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
    }

    // Calculate main period in days
    let totalDays = Math.floor((adjustedEndDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Add additional periods
    values.additionalPeriods.forEach(period => {
      if (period.startDate && period.endDate) {
        const periodStart = new Date(period.startDate);
        const periodEnd = new Date(period.endDate);
        const periodDays = Math.floor((periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24)) + 1; // Include end date
        totalDays += periodDays;
      }
    });

    // Calculate years, months, days
    const years = Math.floor(totalDays / 365);
    const remainingDays = totalDays % 365;
    const months = Math.floor(remainingDays / 30);
    const days = remainingDays % 30;

    // Calculate benefit years (for retirement with multiplier)
    const benefitMultiplier = parseFloat(values.benefitMultiplier);
    const benefitTotalDays = totalDays * benefitMultiplier;
    const benefitYears = Math.floor(benefitTotalDays / 365);
    const benefitRemainingDays = benefitTotalDays % 365;
    const benefitMonths = Math.floor(benefitRemainingDays / 30);
    const benefitDays = benefitRemainingDays % 30;

    // Check retirement eligibility (simplified - 40 years for men, 38 for women)
    const retirementEligible = benefitYears >= 40;
    let retirementMessage = '';
    
    if (retirementEligible) {
      retirementMessage = 'Ispunjavate uvjete za mirovinu po stažu.';
    } else {
      const yearsLeft = 40 - benefitYears;
      retirementMessage = `Potrebno je još ${yearsLeft} godina staža za mirovinu.`;
    }

    return {
      years,
      months,
      days,
      totalDays,
      benefitYears,
      benefitMonths,
      benefitDays,
      retirementEligible,
      retirementMessage
    };
  };

  const handleInputChange = (field: keyof WorkExperienceState, value: string | boolean) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAdditionalPeriodChange = (id: number, field: 'startDate' | 'endDate', value: string) => {
    setValues(prev => ({
      ...prev,
      additionalPeriods: prev.additionalPeriods.map(period => 
        period.id === id ? { ...period, [field]: value } : period
      )
    }));
  };

  const addPeriod = () => {
    setValues(prev => ({
      ...prev,
      additionalPeriods: [
        ...prev.additionalPeriods,
        { id: nextId, startDate: '', endDate: '' }
      ]
    }));
    setNextId(prev => prev + 1);
  };

  const removePeriod = (id: number) => {
    setValues(prev => ({
      ...prev,
      additionalPeriods: prev.additionalPeriods.filter(period => period.id !== id)
    }));
  };

  useEffect(() => {
    if (values.startDate) {
      const results = calculateWorkExperience();
      setResults(results);
    }
  }, [values]);

  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('hr-HR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Radnog Staža",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte svoj radni staž i provjerite uvjete za mirovinu. Uključuje izračun beneficiranog radnog staža i mogućnost dodavanja više razdoblja.",
    "url": "https://online-kalkulator.com/kalkulator-radnog-staza",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun radnog staža",
      "Izračun beneficiranog staža",
      "Provjera uvjeta za mirovinu",
      "Dodavanje više razdoblja",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Radnog Staža | Izračun Radnog Staža i Uvjeta za Mirovinu"
        description="Izračunajte svoj radni staž i provjerite uvjete za mirovinu. Uključuje izračun beneficiranog radnog staža i mogućnost dodavanja više razdoblja."
        canonicalUrl="/kalkulator-radnog-staza"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calendar className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Radnog Staža</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Main Period */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-3">Glavno razdoblje</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Datum početka
                    </label>
                    <input
                      type="date"
                      value={values.startDate}
                      onChange={(e) => handleInputChange('startDate', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Datum završetka
                    </label>
                    <input
                      type="date"
                      value={values.endDate}
                      onChange={(e) => handleInputChange('endDate', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={values.includeEndDate}
                      onChange={(e) => handleInputChange('includeEndDate', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Uključi završni datum</span>
                  </label>
                </div>
              </div>

              {/* Additional Periods */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-lg font-semibold text-gray-800">Dodatna razdoblja</h2>
                  <button
                    onClick={addPeriod}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                  >
                    + Dodaj razdoblje
                  </button>
                </div>
                
                {values.additionalPeriods.length > 0 ? (
                  <div className="space-y-4">
                    {values.additionalPeriods.map(period => (
                      <div key={period.id} className="grid grid-cols-5 gap-2 items-end border p-3 rounded-lg">
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Datum početka
                          </label>
                          <input
                            type="date"
                            value={period.startDate}
                            onChange={(e) => handleAdditionalPeriodChange(period.id, 'startDate', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Datum završetka
                          </label>
                          <input
                            type="date"
                            value={period.endDate}
                            onChange={(e) => handleAdditionalPeriodChange(period.id, 'endDate', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <button
                          onClick={() => removePeriod(period.id)}
                          className="px-3 py-1 text-sm bg-red-100 text-red-600 rounded hover:bg-red-200 transition"
                        >
                          Ukloni
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    Nema dodatnih razdoblja. Kliknite "Dodaj razdoblje" za dodavanje prethodnih zaposlenja.
                  </p>
                )}
              </div>

              {/* Benefit Multiplier */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beneficirani radni staž
                </label>
                <select
                  value={values.benefitMultiplier}
                  onChange={(e) => handleInputChange('benefitMultiplier', e.target.value as WorkExperienceState['benefitMultiplier'])}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="1">Bez beneficiranog staža (1:1)</option>
                  <option value="1.25">Beneficirani staž 12/15 (1:1.25)</option>
                  <option value="1.5">Beneficirani staž 12/18 (1:1.5)</option>
                </select>
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Godine</div>
                      <div className="text-xl font-bold text-blue-600">{results.years}</div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Mjeseci</div>
                      <div className="text-xl font-bold text-blue-600">{results.months}</div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Dani</div>
                      <div className="text-xl font-bold text-blue-600">{results.days}</div>
                    </div>
                  </div>

                  {values.benefitMultiplier !== '1' && (
                    <div className="mt-4">
                      <h3 className="text-md font-medium text-gray-700 mb-2">S beneficiranim stažem:</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-green-50 p-4 rounded-lg text-center">
                          <div className="text-gray-600 text-sm mb-1">Godine</div>
                          <div className="text-xl font-bold text-green-600">{results.benefitYears}</div>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg text-center">
                          <div className="text-gray-600 text-sm mb-1">Mjeseci</div>
                          <div className="text-xl font-bold text-green-600">{results.benefitMonths}</div>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg text-center">
                          <div className="text-gray-600 text-sm mb-1">Dani</div>
                          <div className="text-xl font-bold text-green-600">{results.benefitDays}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="text-center py-3 px-4 rounded-lg bg-gray-50">
                    <p className={`text-lg ${results.retirementEligible ? 'text-green-600' : 'text-blue-600'}`}>
                      {results.retirementMessage}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Radnog Staža</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator radnog staža je koristan alat koji vam pomaže izračunati ukupno trajanje
                  vašeg radnog staža, uključujući mogućnost dodavanja više razdoblja zaposlenja i
                  izračun beneficiranog radnog staža. Također vam pruža informacije o ispunjavanju
                  uvjeta za mirovinu na temelju duljine staža.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite datum početka i završetka glavnog razdoblja zaposlenja</li>
                  <li>Označite "Uključi završni datum" ako želite da se završni datum računa u staž</li>
                  <li>Dodajte dodatna razdoblja zaposlenja klikom na "Dodaj razdoblje" (ako ih imate)</li>
                  <li>Odaberite odgovarajući beneficirani staž ako radite na radnom mjestu s beneficiranim stažem</li>
                  <li>Rezultati će se automatski izračunati</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Beneficirani Radni Staž</h2>
                <p className="text-gray-600 mb-4">
                  Beneficirani radni staž je posebna pogodnost za određena zanimanja koja se smatraju
                  teškima, opasnima ili štetnima za zdravlje. Za svaki period rada na takvim radnim
                  mjestima, radniku se računa uvećani staž.
                </p>
                <div className="space-y-3">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="font-medium text-gray-700">Bez beneficiranog staža (1:1)</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Standardni izračun gdje se 12 mjeseci rada računa kao 12 mjeseci staža.
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="font-medium text-gray-700">Beneficirani staž 12/15 (1:1.25)</p>
                    <p className="text-gray-600 text-sm mt-1">
                      12 mjeseci rada računa se kao 15 mjeseci staža. Primjenjuje se za zanimanja poput
                      policijskih službenika, vatrogasaca, medicinskih sestara u intenzivnoj njezi i sl.
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="font-medium text-gray-700">Beneficirani staž 12/18 (1:1.5)</p>
                    <p className="text-gray-600 text-sm mt-1">
                      12 mjeseci rada računa se kao 18 mjeseci staža. Primjenjuje se za najteža i najopasnija
                      zanimanja poput ronilaca, minera, pilota borbenih zrakoplova i sl.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Uvjeti za Mirovinu</h2>
                <p className="text-gray-600 mb-4">
                  Uvjeti za odlazak u mirovinu u Hrvatskoj i okolnim zemljama uključuju kombinaciju
                  godina života i radnog staža. Ovo su općeniti uvjeti, ali mogu se razlikovati ovisno
                  o specifičnim okolnostima i zakonskim promjenama:
                </p>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Starosna mirovina</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>65 godina života i najmanje 15 godina staža</li>
                      <li>Žene trenutno imaju prijelazno razdoblje s nižom dobnom granicom</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Prijevremena starosna mirovina</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>60 godina života i 35 godina staža (muškarci)</li>
                      <li>Žene trenutno imaju prijelazno razdoblje s nižim uvjetima</li>
                      <li>Uključuje trajno umanjenje mirovine</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Mirovina za dugogodišnjeg osiguranika</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>60 godina života i 41 godinu staža</li>
                      <li>Bez umanjenja mirovine</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračuni su informativne prirode</li>
                  <li>Za točne informacije o vašem radnom stažu, obratite se HZMO-u ili odgovarajućoj instituciji u vašoj zemlji</li>
                  <li>Mirovinski propisi se mijenjaju, pa uvijek provjerite aktualne uvjete</li>
                  <li>Kalkulator ne uzima u obzir specifične okolnosti poput rodiljnog dopusta, bolovanja i sl.</li>
                  <li>Za preciznu procjenu uvjeta za mirovinu, konzultirajte stručnjaka</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Što se Računa u Radni Staž</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Razdoblja osiguranja za koja su plaćeni doprinosi</li>
                  <li>Razdoblja provedena na rodiljnom/roditeljskom dopustu</li>
                  <li>Razdoblja provedena na bolovanju na teret zdravstvenog osiguranja</li>
                  <li>Razdoblja nezaposlenosti uz primanje naknade</li>
                  <li>Razdoblja služenja vojnog roka (za starije generacije)</li>
                  <li>Posebni staž (npr. vrijeme provedeno u Domovinskom ratu)</li>
                  <li>Dokupljeni staž</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Praćenje Radnog Staža</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Redovito provjeravajte svoj radni staž u evidenciji mirovinskog osiguranja</li>
                  <li>Čuvajte radne knjižice, ugovore o radu i druge dokumente koji dokazuju vaš radni staž</li>
                  <li>Prijavite nepravilnosti u evidenciji što prije ih uočite</li>
                  <li>Informirajte se o mogućnostima dokupa staža ako vam nedostaje za mirovinu</li>
                  <li>Pratite promjene u mirovinskom sustavu koje bi mogle utjecati na vaša prava</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorRadnogStaza;